/**
 * AdminGrokImport — Paste a public share-link to a Grok inspection
 * (e.g. fieldradarpro.grok.me/r/xxx), preview photos + PDF + rating
 * counts, pick a WO or Unit to attach to, and commit.
 *
 * Backend: /api/grok-import/preview + /api/grok-import/attach
 */
import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  LinkSimple, ArrowRight, House, Briefcase, Image as ImageIcon,
  FilePdf, MagnifyingGlass, CheckCircle, Warning,
} from "@phosphor-icons/react";

export default function AdminGrokImport() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // backend response
  const [selectedPhotos, setSelectedPhotos] = useState(() => new Set());
  const [attachPdf, setAttachPdf] = useState(true);
  const [note, setNote] = useState("");

  // Target picker (WO or Unit)
  const [targetMode, setTargetMode] = useState("job"); // "job" | "unit"
  const [targetId, setTargetId] = useState("");
  const [targetLabel, setTargetLabel] = useState("");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const doPreview = async () => {
    if (!url.trim()) { toast.error("Paste a share link first"); return; }
    setBusy(true); setPreview(null); setSelectedPhotos(new Set()); setLastResult(null);
    try {
      const r = await api.post("/grok-import/preview", {
        url: url.trim(),
        job_id: targetMode === "job" ? targetId : undefined,
        unit_id: targetMode === "unit" ? targetId : undefined,
      });
      setPreview(r.data);
      // Pre-select every photo
      setSelectedPhotos(new Set((r.data.photo_urls || []).map((_, i) => i)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Preview failed"));
    } finally { setBusy(false); }
  };

  // Target search — jobs by WO# / address, units by address
  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    let cancelled = false;
    const run = async () => {
      setSearching(true);
      try {
        if (targetMode === "job") {
          const r = await api.get(`/jobs?limit=20&q=${encodeURIComponent(search.trim())}`);
          if (!cancelled) setResults((r.data || []).slice(0, 20).map(j => ({
            id: j.id, label: `WO ${j.work_order_number || j.id?.slice(0, 6)} · ${j.property_address || ""}${j.unit ? ` · ${j.unit}` : ""}`,
            sub: j.title || j.description || "",
          })));
        } else {
          const r = await api.get(`/properties/list?limit=25&q=${encodeURIComponent(search.trim())}`);
          if (!cancelled) setResults((r.data || r.data?.properties || []).slice(0, 25).map(u => ({
            id: u.id || u._id, label: `${u.property_address || ""}${u.unit ? ` · ${u.unit}` : ""}`,
            sub: "",
          })));
        }
      } catch (e) {
        if (!cancelled) setResults([]);
      } finally { if (!cancelled) setSearching(false); }
    };
    const t = setTimeout(run, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, targetMode]);

  const togglePhoto = (idx) => {
    setSelectedPhotos(prev => {
      const n = new Set(prev);
      if (n.has(idx)) n.delete(idx); else n.add(idx);
      return n;
    });
  };
  const selectAllPhotos = () => setSelectedPhotos(new Set((preview?.photo_urls || []).map((_, i) => i)));
  const clearPhotoSelection = () => setSelectedPhotos(new Set());

  const commit = async () => {
    if (!targetId) { toast.error("Pick a WO or Unit to attach to"); return; }
    if (!preview) return;
    const photos = (preview.photo_urls || []).filter((_, i) => selectedPhotos.has(i));
    setAttaching(true);
    try {
      const payload = {
        url: preview.url,
        job_id: targetMode === "job" ? targetId : undefined,
        unit_id: targetMode === "unit" ? targetId : undefined,
        photos,
        pdf_url: attachPdf ? (preview.pdf_urls?.[0] || null) : null,
        note: note.trim() || null,
      };
      const r = await api.post("/grok-import/attach", payload);
      setLastResult(r.data);
      toast.success(`Attached ${r.data.photos_attached} photo${r.data.photos_attached === 1 ? "" : "s"}${r.data.pdf_attached ? " + PDF" : ""} to ${r.data.attached_to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Attach failed"));
    } finally { setAttaching(false); }
  };

  const totalRatings = useMemo(() => {
    if (!preview?.rating_counts) return 0;
    return Object.values(preview.rating_counts).reduce((n, v) => n + v, 0);
  }, [preview]);

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="grok-import-page">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white flex items-center justify-center">
            <LinkSimple weight="bold" size={22}/>
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#0B1F3A] tracking-tight">Grok Share-Link Importer</h1>
            <div className="text-xs text-slate-500">
              Paste a public inspection link (e.g. <code className="text-fuchsia-700">fieldradarpro.grok.me/r/…</code>) → pick a WO or Unit → attach photos &amp; PDF
            </div>
          </div>
        </div>

        {/* Step 1 — URL + target */}
        <div className="rounded-xl border-2 border-slate-200 bg-white p-4">
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2">Step 1 · Share link</div>
          <div className="flex gap-2">
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://fieldradarpro.grok.me/r/xxxxxxxx"
              data-testid="grok-url-input"
              className="flex-1 h-11 px-3 border-2 border-[#0f172a] text-sm font-medium focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40"/>
            <button onClick={doPreview} disabled={busy || !url.trim()}
              data-testid="grok-preview-btn"
              className="h-11 px-5 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest">
              {busy ? "Fetching…" : "Preview"} <ArrowRight weight="bold" size={14} className="inline ml-1"/>
            </button>
          </div>

          {/* Target picker */}
          <div className="mt-4">
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2">Attach to</div>
            <div className="flex gap-1 mb-2">
              {[["job", "Work Order", Briefcase], ["unit", "Unit", House]].map(([k, label, Icon]) => (
                <button key={k} onClick={() => { setTargetMode(k); setTargetId(""); setTargetLabel(""); setSearch(""); setResults([]); }}
                  data-testid={`grok-target-mode-${k}`}
                  className={`h-9 px-3 border-2 text-xs font-black uppercase tracking-widest flex items-center gap-1.5 ${
                    targetMode === k ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0f172a]"
                  }`}>
                  <Icon weight="bold" size={14}/>{label}
                </button>
              ))}
            </div>
            {targetId ? (
              <div className="flex items-center gap-2 p-2 rounded border-2 border-emerald-500 bg-emerald-50">
                <CheckCircle weight="fill" size={18} className="text-emerald-600"/>
                <span className="text-sm font-bold text-emerald-900 flex-1 truncate" data-testid="grok-target-label">{targetLabel}</span>
                <button onClick={() => { setTargetId(""); setTargetLabel(""); setSearch(""); }}
                  data-testid="grok-target-clear"
                  className="text-xs font-black uppercase tracking-widest text-emerald-700 hover:text-emerald-900">Change</button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <MagnifyingGlass weight="bold" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder={targetMode === "job" ? "Search by WO#, address, or title" : "Search by address"}
                    data-testid="grok-target-search"
                    className="w-full h-10 pl-9 pr-3 border-2 border-slate-200 text-sm focus:outline-none focus:border-fuchsia-500"/>
                </div>
                {search.trim() && (
                  <div className="mt-1 border-2 border-slate-200 bg-white max-h-56 overflow-y-auto"
                    data-testid="grok-target-results">
                    {searching && <div className="p-3 text-xs text-slate-500">Searching…</div>}
                    {!searching && results.length === 0 && <div className="p-3 text-xs text-slate-500">No matches</div>}
                    {results.map(r => (
                      <button key={r.id} onClick={() => { setTargetId(r.id); setTargetLabel(r.label); setResults([]); setSearch(""); }}
                        data-testid={`grok-target-pick-${r.id}`}
                        className="w-full text-left p-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                        <div className="text-sm font-semibold text-[#0B1F3A] truncate">{r.label}</div>
                        {r.sub && <div className="text-[11px] text-slate-500 truncate">{r.sub}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Step 2 — Preview */}
        {preview && (
          <div className="rounded-xl border-2 border-slate-200 bg-white p-4 space-y-3" data-testid="grok-preview-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">Step 2 · Preview</div>
                <div className="text-base font-bold text-[#0B1F3A] mt-0.5" data-testid="grok-preview-title">
                  {preview.title || "(no title)"}
                </div>
                {preview.description && <div className="text-xs text-slate-600 mt-0.5">{preview.description}</div>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-fuchsia-800 bg-fuchsia-100 px-2 py-1 rounded-full flex items-center gap-1" data-testid="grok-photos-count">
                  <ImageIcon weight="bold" size={12}/> {preview.photos_found} photo{preview.photos_found === 1 ? "" : "s"}
                </span>
                {preview.pdf_urls.length > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-red-800 bg-red-100 px-2 py-1 rounded-full flex items-center gap-1" data-testid="grok-pdf-count">
                    <FilePdf weight="bold" size={12}/> {preview.pdf_urls.length} PDF
                  </span>
                )}
                {totalRatings > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-800 bg-slate-200 px-2 py-1 rounded-full" data-testid="grok-ratings-count">
                    {totalRatings} rating keyword{totalRatings === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </div>

            {/* Rating counts */}
            {totalRatings > 0 && (
              <div className="flex gap-1.5 flex-wrap">
                {Object.entries(preview.rating_counts).map(([word, n]) => (
                  n > 0 ? (
                    <span key={word} className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      word === "great" ? "bg-emerald-100 text-emerald-800" :
                      word === "good" ? "bg-cyan-100 text-cyan-800" :
                      word === "fair" ? "bg-amber-100 text-amber-800" :
                      word === "poor" ? "bg-orange-100 text-orange-800" :
                      "bg-red-100 text-red-800"
                    }`}>
                      {word} · {n}
                    </span>
                  ) : null
                ))}
              </div>
            )}

            {/* Photo grid */}
            {preview.photo_urls.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Photos · {selectedPhotos.size} / {preview.photo_urls.length} selected
                  </div>
                  <div className="flex gap-1">
                    <button onClick={selectAllPhotos}
                      data-testid="grok-select-all"
                      className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 hover:text-fuchsia-900">
                      Select all
                    </button>
                    <span className="text-slate-300">·</span>
                    <button onClick={clearPhotoSelection}
                      data-testid="grok-clear-selection"
                      className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900">
                      Clear
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
                  {preview.photo_urls.map((pu, i) => (
                    <button key={pu} onClick={() => togglePhoto(i)}
                      data-testid={`grok-photo-${i}`}
                      className={`relative aspect-square overflow-hidden rounded border-2 transition-colors ${
                        selectedPhotos.has(i) ? "border-fuchsia-500 ring-2 ring-fuchsia-500/40" : "border-slate-200"
                      }`}>
                      <img src={pu} alt={`Grok photo ${i+1}`} loading="lazy" className="w-full h-full object-cover"
                           onError={(e) => { e.target.style.opacity = "0.3"; }}/>
                      {selectedPhotos.has(i) && (
                        <div className="absolute top-1 right-1 h-5 w-5 bg-fuchsia-500 text-white rounded-full flex items-center justify-center text-[11px] font-black">✓</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* PDF selector */}
            {preview.pdf_urls.length > 0 && (
              <div className="rounded-lg border-2 border-red-200 bg-red-50 p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)}
                    data-testid="grok-attach-pdf"
                    className="h-4 w-4"/>
                  <FilePdf weight="bold" size={16} className="text-red-700"/>
                  <span className="text-sm font-bold text-red-900">Attach PDF report</span>
                  <span className="text-[11px] text-red-700 truncate flex-1">{preview.pdf_urls[0]}</span>
                </label>
              </div>
            )}

            {/* Note */}
            <div>
              <label className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                Note (optional — e.g. "Grok inspection 2026-02-14")
              </label>
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                data-testid="grok-note"
                maxLength={200}
                className="w-full h-10 px-3 border-2 border-slate-200 text-sm mt-1 focus:outline-none focus:border-fuchsia-500"/>
            </div>

            {/* Commit */}
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-200">
              <div className="text-xs text-slate-500">
                {targetId ? (
                  <>Will attach to <b>{targetLabel}</b></>
                ) : (
                  <span className="text-amber-700 font-bold flex items-center gap-1">
                    <Warning weight="bold" size={14}/> Pick a WO or Unit above to continue
                  </span>
                )}
              </div>
              <button onClick={commit} disabled={attaching || !targetId || selectedPhotos.size === 0 && !attachPdf}
                data-testid="grok-attach-btn"
                className="h-11 px-5 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest">
                {attaching ? "Attaching…" : `Attach ${selectedPhotos.size} photo${selectedPhotos.size === 1 ? "" : "s"}${attachPdf && preview.pdf_urls.length ? " + PDF" : ""}`}
              </button>
            </div>
          </div>
        )}

        {/* Last result */}
        {lastResult && (
          <div className="rounded-xl border-2 border-emerald-400 bg-emerald-50 p-4" data-testid="grok-result-panel">
            <div className="flex items-center gap-2 text-emerald-900">
              <CheckCircle weight="fill" size={22} className="text-emerald-600"/>
              <div>
                <div className="font-bold text-sm">Attached to {lastResult.attached_to}</div>
                <div className="text-xs">
                  {lastResult.photos_attached} photo{lastResult.photos_attached === 1 ? "" : "s"} ·
                  {" "}{lastResult.pdf_attached ? "1 PDF" : "no PDF"}
                  {lastResult.failures > 0 ? ` · ${lastResult.failures} failed to download` : ""}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
